import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { CookieBanner } from "@/components/marketing/CookieBanner";
import { ArrowRight, Menu, X } from "lucide-react";
import { TikTokIcon, InstagramIcon } from "./marketing/icons";

const links = [["/features", "Features"], ["/how-it-works", "How it works"], ["/creators", "Creators"], ["/solutions", "Solutions"], ["/case-studies", "Customers"], ["/pricing", "Pricing"], ["/resources", "Resources"]];

const TITLES = {
  "/": "HypeFlow AI — AI Creator Marketing for TikTok & Instagram | Hypeflow B.V.",
  "/features": "Features — The complete creator operating system | HypeFlow AI",
  "/how-it-works": "How it works — Idea to viral campaign in 4 moves | HypeFlow AI",
  "/creators": "Creator directory — Browse TikTok & Instagram creators | HypeFlow AI",
  "/solutions": "Solutions by industry — Creator strategies | HypeFlow AI",
  "/case-studies": "Customer case studies — Real creator campaign results | HypeFlow AI",
  "/pricing": "Pricing — Simple, scalable creator marketing plans | HypeFlow AI",
  "/resources": "Resources — The creator marketing playbook | HypeFlow AI",
  "/about": "About — The AI creator engine for APAC | HypeFlow AI",
  "/contact": "Contact — Book a HypeFlow AI demo | Hypeflow B.V.",
  "/privacy": "Privacy Policy | HypeFlow AI",
  "/cookies": "Cookie Policy | HypeFlow AI",
  "/terms": "Terms of Service | HypeFlow AI",
};

const Logo = ({ testid }) => (
  <Link to="/" className="mk-logo" data-testid={testid}>
    <img src="/hypeflow-icon.png" alt="HypeFlow AI" className="mk-logo-img" />
    <span><b>HypeFlow</b> <span className="grad-text">AI</span></span>
  </Link>
);

export default function MarketingLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    let title = TITLES[pathname];
    if (!title) {
      if (pathname.startsWith("/case-studies/")) title = "Case study | HypeFlow AI";
      else if (pathname.startsWith("/resources/")) title = "Resources | HypeFlow AI";
    }
    document.title = title || TITLES["/"];
    window.scrollTo(0, 0);
    setOpen(false);
  }, [pathname]);
  return (
    <div className="marketing-site">
      <header className="mk-nav" data-testid="marketing-navigation">
        <div className="mk-nav-in">
          <Logo testid="marketing-logo-link" />
          <nav className={open ? "open" : ""} data-testid="marketing-navigation-links">
            {links.map(([to, label]) => (
              <NavLink key={to} to={to} onClick={() => setOpen(false)} data-testid={`marketing-nav-${label.toLowerCase().replace(/\s/g, "-")}`}>{label}</NavLink>
            ))}
            <Link to="/login?mode=register" className="mk-btn primary sm mobile-cta" style={{ display: "none" }} onClick={() => setOpen(false)}>Start free</Link>
          </nav>
          <div className="mk-nav-right">
            <Link to="/login" className="mk-login" data-testid="marketing-login-link">Log in</Link>
            <Link to="/login?mode=register" className="mk-btn primary sm" data-testid="marketing-start-trial-nav">Start free <ArrowRight size={15} /></Link>
            <button className="mk-menu" onClick={() => setOpen(!open)} data-testid="marketing-mobile-menu" aria-label="Menu">{open ? <X /> : <Menu />}</button>
          </div>
        </div>
      </header>

      <Outlet />

      <footer className="mk-footer" data-testid="marketing-footer">
        <div className="mk-wrap">
          <div className="mk-footer-grid">
            <div className="mk-footer-lead">
              <Logo testid="footer-logo" />
              <p>The AI creator engine for TikTok & Instagram campaigns. Discover, run, and prove creator marketing that actually moves.</p>
              <address className="mk-footer-address" data-testid="footer-address">Hypeflow B.V.<br />Herengracht 320<br />1016 CE Amsterdam<br />The Netherlands</address>
              <div className="mk-footer-social">
                <a href="#" data-testid="footer-tiktok" aria-label="TikTok"><TikTokIcon size={18} /></a>
                <a href="#" data-testid="footer-instagram" aria-label="Instagram"><InstagramIcon size={18} /></a>
              </div>
            </div>
            <div className="mk-footer-col">
              <b>Product</b>
              <Link to="/features" data-testid="footer-features">Features</Link>
              <Link to="/how-it-works" data-testid="footer-how">How it works</Link>
              <Link to="/creators" data-testid="footer-creators">Creator directory</Link>
              <Link to="/case-studies" data-testid="footer-cases">Case studies</Link>
              <Link to="/pricing" data-testid="footer-pricing">Pricing</Link>
            </div>
            <div className="mk-footer-col">
              <b>Company</b>
              <Link to="/about" data-testid="footer-about">About</Link>
              <Link to="/solutions" data-testid="footer-solutions">Solutions</Link>
              <Link to="/resources" data-testid="footer-resources">Resources</Link>
              <Link to="/contact" data-testid="footer-contact">Contact</Link>
            </div>
            <div className="mk-footer-col">
              <b>Get started</b>
              <p>Find creators your audience already trusts.</p>
              <Link to="/login?mode=register" className="mk-btn primary sm" data-testid="footer-start-trial" style={{ marginBottom: 16 }}>Start free <ArrowRight size={15} /></Link>
              <span className="mk-footer-status"><i /> All systems operational</span>
            </div>
          </div>
          <div className="mk-footer-bottom">
            <span>© 2026 Hypeflow B.V. · HypeFlow AI</span>
            <div className="mk-footer-legal">
              <Link to="/privacy" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms">Terms of Service</Link>
              <Link to="/cookies" data-testid="footer-cookies">Cookie Policy</Link>
            </div>
            <span>Based in Amsterdam · Running creator campaigns worldwide</span>
          </div>
        </div>
      </footer>
      <CookieBanner />
    </div>
  );
}
